import { CompressedSearchStateMachine as CompressedSearchStateMachineNodes } from "../lib/EfficientMultiStringSearch";
import { NetworkEvent } from "../lib/Events";
import { EncodedSearchTermWithEncodingSteps } from "../lib/Search/EncodedSearchTerm";
import { EventSearchResultsOrEventIndexOnly as EventSearchResultsOrEventIndexOnly, NetworkSearchScopeOptions, SearchContextOptions } from "../lib/searchNetworkEvent";
import { DbRecordedEvent, UUID } from "../State/EvidenceDB/DatabaseTypes";


const canonicalSearchTermItemSeparator = "\0;" as const;
const CanonicalSearchTermStringPrefix = `CanonicalSearchTermString:` as const;
type CanonicalSearchTermStringPrefix = typeof CanonicalSearchTermStringPrefix;
export const CanonicalSearchTermsString = (searchTerms: string[]) => 
	`${CanonicalSearchTermStringPrefix}${[...new Set<string>(searchTerms.filter( x => x.length > 0).toSorted())].join(canonicalSearchTermItemSeparator)}` as const;
export type CanonicalSearchTermsString = ReturnType<typeof CanonicalSearchTermsString>;
export const isCanonicalSearchTermsString = (s: unknown): s is CanonicalSearchTermsString =>
	typeof s === 'string' && s.startsWith(CanonicalSearchTermStringPrefix);
export const deserializeCanonicalSearchTermsString = (s: CanonicalSearchTermsString): string[] =>
 	s.slice(CanonicalSearchTermStringPrefix.length).split(canonicalSearchTermItemSeparator).filter(x => x.length > 0);


export const StartWorkerRequestType = 'start-worker' as const;
export type StartWorkerRequestType = typeof StartWorkerRequestType;
export const CancelOutstandingRequestsRequestType = 'cancel-outstanding-requests' as const;
export type CancelOutstandingRequestsRequestType = typeof CancelOutstandingRequestsRequestType;
export const GetEncodingsOfStringsToSearchForRequestType = 'get-encodings-of-strings-to-search-for' as const;
export type GetEncodingsOfStringsToSearchForRequestType = typeof GetEncodingsOfStringsToSearchForRequestType;
export const CreateAndUpdateSearchFunctionRequestType = 'create-and-update-search-function' as const;
export type CreateAndUpdateSearchFunctionRequestType = typeof CreateAndUpdateSearchFunctionRequestType;
export const UpdateSearchFunctionRequestType = 'update-search-function' as const;
export type UpdateSearchFunctionRequestType = typeof UpdateSearchFunctionRequestType;
export const SearchNetworkEventRequestType = 'search-network-event' as const;
export type SearchNetworkEventRequestType = typeof SearchNetworkEventRequestType;

const PoolWorkerMetaRequestTypes = [
	StartWorkerRequestType,
 	CancelOutstandingRequestsRequestType,
] as const;
export type PoolWorkerMetaRequestType = typeof PoolWorkerMetaRequestTypes[number];

export const QueueablePoolWorkerRequestTypes = [
	GetEncodingsOfStringsToSearchForRequestType,
 	CreateAndUpdateSearchFunctionRequestType,
 	UpdateSearchFunctionRequestType,
 	SearchNetworkEventRequestType,
] as const;
export type QueueablePoolWorkerRequestType = typeof QueueablePoolWorkerRequestTypes[number];

const PoolWorkerRequestTypes = [
	...PoolWorkerMetaRequestTypes,
	...QueueablePoolWorkerRequestTypes,
] as const;
export type PoolWorkerRequestType = typeof PoolWorkerRequestTypes[number];

export interface PoolWorkerRequestBase<T extends PoolWorkerRequestType> {
	type: T;
	direction: 'request';
	requestUuid: UUID;
}

export interface PoolWorkerResponseBase<T extends PoolWorkerRequestType> {
 type: T;
	direction: 'response';
	requestUuid: UUID;
}

export interface PoolWorkerExceptionResponse<T extends PoolWorkerRequestType = PoolWorkerMetaRequestType> extends PoolWorkerResponseBase<T> {
	type: T;
	exception: true;
	error: string;
}

export interface StartWorkerRequest extends PoolWorkerRequestBase<StartWorkerRequestType> {
	workerId: number;
}

export interface StartWorkerResponse extends PoolWorkerResponseBase<StartWorkerRequestType> {
	workerId: number;
}

export interface CancelOutstandingRequestsRequest extends PoolWorkerRequestBase<CancelOutstandingRequestsRequestType> {
	onlyCancelRequestsOfType?: readonly QueueablePoolWorkerRequestType[];
}

export interface CancelOutstandingRequestsResponse extends PoolWorkerResponseBase<CancelOutstandingRequestsRequestType> {}

export interface GetEncodingsOfStringsToSearchForRequest extends PoolWorkerRequestBase<GetEncodingsOfStringsToSearchForRequestType> {
	stringOrStringsToSearchFor: string | string[];
};

export interface GetEncodingsOfStringsToSearchForResponse extends PoolWorkerResponseBase<GetEncodingsOfStringsToSearchForRequestType> {
	encodings: EncodedSearchTermWithEncodingSteps[];
};

export interface CreateAndUpdateSearchFunctionRequest extends PoolWorkerRequestBase<CreateAndUpdateSearchFunctionRequestType> {
	canonicalSearchTermsString: CanonicalSearchTermsString;
	encodings: EncodedSearchTermWithEncodingSteps[];
	searchOptions?: NetworkSearchScopeOptions & SearchContextOptions;
};

export interface CreateAndUpdateSearchFunctionResponse extends PoolWorkerResponseBase<CreateAndUpdateSearchFunctionRequestType> {
	encodings: EncodedSearchTermWithEncodingSteps[];
	compressedSearchStateMachine: CompressedSearchStateMachineNodes;
}

export interface UpdateSearchFunctionRequest extends PoolWorkerRequestBase<UpdateSearchFunctionRequestType> {
	encodings: EncodedSearchTermWithEncodingSteps[];
	canonicalSearchTermsString: CanonicalSearchTermsString;
	searchOptions?: NetworkSearchScopeOptions & SearchContextOptions;
	compressedSearchStateMachine: CompressedSearchStateMachineNodes;
};

export interface UpdateSearchFunctionResponse extends PoolWorkerResponseBase<UpdateSearchFunctionRequestType> {
	success: boolean;
}

export interface SearchNetworkEventRequest extends PoolWorkerRequestBase<SearchNetworkEventRequestType> {
	networkEvent: DbRecordedEvent<NetworkEvent>;
}

export interface SearchNetworkEventResponse extends PoolWorkerResponseBase<SearchNetworkEventRequestType> {
	searchResults: EventSearchResultsOrEventIndexOnly;
};

export type MetaPoolWorkerRequest =
	StartWorkerRequest |
	CancelOutstandingRequestsRequest |
	never;

export type QueueablePoolWorkerRequest =
	GetEncodingsOfStringsToSearchForRequest |
	CreateAndUpdateSearchFunctionRequest |
	UpdateSearchFunctionRequest |
	SearchNetworkEventRequest |
	never;

type PoolWorkerRequests =
	MetaPoolWorkerRequest |
	QueueablePoolWorkerRequest |
	never;

export type PoolWorkerRequest<T extends PoolWorkerRequestType = PoolWorkerRequestType> = Extract<PoolWorkerRequests, {type: T}>;

export type QueueablePoolWorkerResponse =
	GetEncodingsOfStringsToSearchForResponse |
	CreateAndUpdateSearchFunctionResponse |
	UpdateSearchFunctionResponse |
	SearchNetworkEventResponse |
	never;

export type MetaPoolWorkerResponse =
	StartWorkerResponse |
	CancelOutstandingRequestsResponse |
	never;

type PoolWorkerResponses =
	MetaPoolWorkerResponse |
	QueueablePoolWorkerResponse |
	never;

export type PoolWorkerResponse<T extends PoolWorkerRequestType = PoolWorkerRequestType> = Extract<PoolWorkerResponses, {type: T}>;

export type PoolWorkerResponseOrException<T extends PoolWorkerRequestType = PoolWorkerRequestType> = PoolWorkerResponse<T> |  PoolWorkerExceptionResponse<T>;



const PoolWorkerResponseTypeSet = new Set<unknown>(PoolWorkerRequestTypes);
export const isPoolWorkerRequestType = (type: unknown): type is PoolWorkerRequestType => PoolWorkerResponseTypeSet.has(type);

export const isPoolWorkerRequest = (u: unknown): u is PoolWorkerRequest =>
	u != null && typeof u === 'object' && 'type' in u && isPoolWorkerRequestType(u.type);

export const isPoolWorkerResponseOrException = (u: unknown): u is PoolWorkerResponseOrException =>
	u != null && typeof u === 'object' && 'type' in u && isPoolWorkerRequestType(u.type);

export const isPoolWorkerException = (u: unknown): u is PoolWorkerExceptionResponse =>
	u != null && typeof u === 'object' && 'type' in u && isPoolWorkerRequestType(u.type) &&
 'exception' in u && u.exception === true;

 export const isPoolWorkerResponseOfType = <T extends PoolWorkerRequestType>(type: T, u: unknown): u is PoolWorkerResponse<T> =>
	u != null && typeof u === 'object' && 'type' in u && u.type === type;
export const isPoolWorkerRequestOfType = <T extends PoolWorkerRequestType>(type: T, u: unknown): u is PoolWorkerRequest<T> =>
	 u != null && typeof u === 'object' && 'type' in u && u.type === type && 'direction' in u && u.direction === 'request';

export type PoolWorkerRequestArguments<T extends PoolWorkerRequestType> = Omit<PoolWorkerRequest<T>, 'requestUuid' | 'direction' | 'type'>;

export const PoolWorkerRequest = <T extends PoolWorkerRequestType>(
	type: T,
	partialRequest: Readonly<PoolWorkerRequestArguments<T>>
): PoolWorkerRequest<T> => ({
	...partialRequest,
	type,
	requestUuid: UUID(),
	direction: 'request'
}) as PoolWorkerRequest<T>;

export interface UnresolvedRequest<T extends PoolWorkerRequestType = PoolWorkerRequestType> {
	request: PoolWorkerRequest<T>;
	// resolve: (response: PoolWorkerResponse<T>) => void;
	resolve: (response: PoolWorkerResponse<T> | PromiseLike<PoolWorkerResponse<T>>) => void;
	reject: (reason?: unknown) => void;
}

export const createUnresolvedRequestAndPromise = <T extends PoolWorkerRequestType>(
	type: T,
	partialRequest: Readonly<PoolWorkerRequestArguments<T>>
): {unresolvedRequest: UnresolvedRequest<T>, promise: Promise<PoolWorkerResponse<T>>} => {
	const request = PoolWorkerRequest<T>(type, partialRequest);
	const unresolvedRequest = {request} as UnresolvedRequest<T>;
	const promise = new Promise<PoolWorkerResponse<T>>((resolve, reject) => {
		unresolvedRequest.resolve = resolve;
		unresolvedRequest.reject = reject;
	});
	return { unresolvedRequest, promise };
};
